import type { SoftphoneCallsHook } from './index.types';
import { CallManager } from 'Modules/softphone/services/CallManager';
import { useSoftphoneStore } from 'Modules/softphone/store';
import { useCallback, useMemo } from 'react';

export type { SoftphoneCallsHook, SessionCallbacks } from './index.types';

/**
 * useSoftphoneCalls - React hook for call operations
 *
 * Lightweight wrapper that subscribes to store state and exposes
 * stable function references from the manager.
 *
 * @note CallManager initialization is handled by ConnectionManager.initializeSip()
 */
export const useSoftphoneCalls = (): SoftphoneCallsHook => {
  const manager = CallManager.getInstance();
  const {
    activeCalls,
    currentCallId,
    status,
    currentCall,
  } = useSoftphoneStore((state) => ({
    activeCalls: state.activeCalls,
    currentCallId: state.currentCallId,
    status: state.status,
    currentCall: state.currentCallId
      ? state.activeCalls.get(state.currentCallId) ?? null
      : null,
  }));

  const active = useMemo(
    () => Array.from(activeCalls.values()),
    [activeCalls]
  );

  const makeCall = useCallback(
    (phoneNumber: string, personId?: string) =>
      manager.makeCall(phoneNumber, personId),
    [manager]
  );

  const hangUp = useCallback(
    (callId: string) => manager.hangUp(callId),
    [manager]
  );

  return {
    active,
    currentId: currentCallId,
    status,
    makeCall,
    hangUp,
    hasActiveCalls: activeCalls.size > 0,
    activeCallCount: activeCalls.size,
    currentCall,
  };
};
